/**
 * BoLessonBilanModal — Modal enseignant pour remplir le bilan pédagogique
 * en fin de leçon. Utilisé depuis BoMyPlanningPage.
 *
 * Flow : sélection compétences travaillées + note progression (1-5) + humeur + message
 * → POST /api/bo/planning/{id}/bilan → leçon passe en 'effectue' avec bilan attaché
 */
import React, { useEffect, useState } from 'react';
import { X, Smile, Meh, Frown, Sparkles, AlertCircle } from 'lucide-react';
import api from '../../api';

const BO_BLUE = '#1d4ed8';

export const BoLessonBilanModal = ({ lesson, onClose, onDone }) => {
  const [competences, setCompetences] = useState([]);
  const [form, setForm] = useState({
    competences_travaillees: [],
    progression_note: 4,
    mood: 'good',
    message: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.boEleveCompetences().then((r) => setCompetences(r.items || []));
  }, []);

  const toggle = (key) => {
    setForm((f) => ({
      ...f,
      competences_travaillees: f.competences_travaillees.includes(key)
        ? f.competences_travaillees.filter((k) => k !== key)
        : [...f.competences_travaillees, key],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.boLessonBilanPost(lesson.id, form);
      onDone?.(`Bilan enregistré pour ${lesson.student_name || 'l\'élève'}`);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-t-2 sm:border-2 border-border rounded-t-2xl sm:rounded-2xl max-w-lg w-full p-5 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        data-testid="modal-bilan-lesson"
      >
        <div className="flex items-center justify-between">
          <div>
            <div
              className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1"
              style={{ color: BO_BLUE }}
            >
              <Sparkles className="w-3 h-3" /> Bilan de leçon
            </div>
            <h2
              className="text-xl font-black leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {lesson.student_name || 'Élève'}
            </h2>
            <div className="text-xs text-foreground/60 mt-0.5">
              {new Date(lesson.date).toLocaleDateString('fr-FR', {
                weekday: 'long', day: 'numeric', month: 'long',
              })}
              {' · '}{lesson.start_time}{' · '}{lesson.duration_min}min
            </div>
          </div>
          <button type="button" onClick={onClose}>
            <X className="w-5 h-5 text-foreground/60" />
          </button>
        </div>

        {/* Compétences (chips) */}
        <div>
          <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
            Compétences travaillées
          </span>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            {competences.map((c) => {
              const on = form.competences_travaillees.includes(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggle(c.key)}
                  className={`text-[10px] font-black text-left px-2 py-2 rounded-lg transition ${
                    on
                      ? 'text-white'
                      : 'bg-muted text-foreground/70 hover:bg-muted/80'
                  }`}
                  style={on ? { background: BO_BLUE } : {}}
                  data-testid={`bilan-comp-${c.key}`}
                >
                  {on ? '✓ ' : ''}{c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note de progression 1-5 */}
        <div>
          <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
            Progression sur cette leçon
          </span>
          <div className="mt-1.5 flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setForm({ ...form, progression_note: n })}
                className={`flex-1 py-3 rounded-lg text-lg font-black transition ${
                  form.progression_note === n
                    ? 'text-white'
                    : 'bg-muted text-foreground/50 hover:bg-muted/80'
                }`}
                style={form.progression_note === n ? { background: BO_BLUE } : {}}
                data-testid={`bilan-note-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-foreground/40 mt-1 px-1">
            <span>Difficile</span>
            <span>Excellent</span>
          </div>
        </div>

        {/* Humeur/état */}
        <div>
          <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
            Ressenti global
          </span>
          <div className="mt-1.5 grid grid-cols-3 gap-1.5">
            {[
              { key: 'good', icon: Smile, label: 'Confiant', color: '#059669' },
              { key: 'neutral', icon: Meh, label: 'Neutre', color: '#f59e0b' },
              { key: 'needs_work', icon: Frown, label: 'À revoir', color: '#dc2626' },
            ].map(({ key, icon: Icon, label, color }) => {
              const on = form.mood === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm({ ...form, mood: key })}
                  className={`flex flex-col items-center gap-1 py-3 rounded-lg transition ${
                    on ? 'text-white' : 'bg-muted text-foreground/60 hover:bg-muted/80'
                  }`}
                  style={on ? { background: color } : {}}
                  data-testid={`bilan-mood-${key}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-black">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Message (optionnel) */}
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
            Un mot pour l&apos;élève (optionnel)
          </span>
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 2000) })}
            rows={3}
            placeholder="Ex : gros progrès sur les trajectoires, à retravailler les angles morts…"
            className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8] resize-none"
            data-testid="bilan-message"
          />
          <div className="text-[9px] text-foreground/40 text-right mt-1">
            {form.message.length}/2000
          </div>
        </label>

        {error && (
          <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-2.5 flex items-start gap-2 text-xs">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" /> {error}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-3 py-3 rounded-lg border-2 border-border text-sm font-black hover:bg-muted"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-[2] px-3 py-3 rounded-lg text-white text-sm font-black hover:opacity-90 disabled:opacity-40"
            style={{ background: BO_BLUE }}
            data-testid="btn-submit-bilan"
          >
            {saving ? 'Enregistrement…' : '✓ Valider & marquer effectuée'}
          </button>
        </div>
      </form>
    </div>
  );
};
